import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { servicesList } from "@/data/services";
import { b2bList } from "@/data/b2b";
import { trackCallConversion } from "@/lib/tracking";

const links = [
  { id: "services", label: "Services", testid: NAV.linkServices, hasDropdown: true },
  { id: "business", label: "For Business", testid: "nav-link-business", hasBusinessDropdown: true },
  { id: "pricing", label: "Pricing", testid: NAV.linkPricing },
  { id: "blog", label: "Blog", testid: "nav-link-blog", isRoute: true, path: "/blog" },
  { id: "contact", label: "Contact", testid: NAV.linkContact },
];

const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [businessOpen, setBusinessOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    setServicesOpen(false);
    setBusinessOpen(false);
    // If on a subroute, go home first then scroll
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-zinc-900 transition-colors ${
        scrolled ? "bg-[#F4F4F5]/95 backdrop-blur" : "bg-[#F4F4F5]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
        >
          <div className="w-11 h-11 bg-white border-2 border-black flex items-center justify-center overflow-hidden hard-shadow-sm">
            <img
              src="https://customer-assets.emergentagent.com/job_site-builder-3572/artifacts/int9cqaw_FE1D1B5F-B5C0-402C-A181-71BFC9E298FA.png"
              alt="Blue Collar World Pest Control logo"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="leading-tight">
            <div className="font-display uppercase text-[15px] font-semibold tracking-tight">
              Blue Collar World
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
              Pest Control · PGH
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.hasDropdown ? (
              <div
                key={l.id}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  data-testid={l.testid}
                  onClick={() => scrollTo(l.id)}
                  className="text-sm uppercase tracking-[0.18em] font-medium hover:text-[#D9480F] transition-colors flex items-center gap-1"
                >
                  {l.label}
                  <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} />
                </button>
                {servicesOpen && (
                  <div className="absolute top-full left-0 pt-2 w-64">
                    <div className="bg-white border-2 border-black hard-shadow-sm">
                      {servicesList.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`nav-service-${s.slug}`}
                          onClick={() => setServicesOpen(false)}
                          className="block px-4 py-3 text-sm font-medium hover:bg-[#F59E0B]/20 border-b border-zinc-200 last:border-0"
                        >
                          {s.title}
                        </Link>
                      ))}
                      <Link
                        to="/vs-competitors"
                        onClick={() => setServicesOpen(false)}
                        className="block px-4 py-3 text-xs uppercase tracking-[0.18em] font-mono font-bold hover:bg-[#0B2B40] hover:text-white bg-[#F4F4F5] border-t-2 border-black"
                      >
                        Compare us vs. big brands →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : l.hasBusinessDropdown ? (
              <div
                key={l.id}
                className="relative"
                onMouseEnter={() => setBusinessOpen(true)}
                onMouseLeave={() => setBusinessOpen(false)}
              >
                <Link
                  to="/for/realtors"
                  data-testid={l.testid}
                  className="text-sm uppercase tracking-[0.18em] font-medium hover:text-[#D9480F] transition-colors flex items-center gap-1"
                >
                  {l.label}
                  <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} />
                </Link>
                {businessOpen && (
                  <div className="absolute top-full left-0 pt-2 w-72">
                    <div className="bg-white border-2 border-black hard-shadow-sm">
                      <div className="px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D9480F] font-bold border-b-2 border-black bg-[#F4F4F5]">
                        B2B Programs
                      </div>
                      {b2bList.map((b) => {
                        const BIcon = b.Icon;
                        return (
                          <Link
                            key={b.slug}
                            to={`/for/${b.slug}`}
                            data-testid={`nav-b2b-${b.slug}`}
                            onClick={() => setBusinessOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium hover:bg-[#F59E0B]/20 border-b border-zinc-200 last:border-0"
                          >
                            <BIcon className="w-4 h-4 text-[#D9480F] flex-shrink-0" strokeWidth={2} />
                            <span>{b.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : l.isRoute ? (
              <Link
                key={l.id}
                to={l.path}
                data-testid={l.testid}
                className="text-sm uppercase tracking-[0.18em] font-medium hover:text-[#D9480F] transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.id}
                data-testid={l.testid}
                onClick={() => scrollTo(l.id)}
                className="text-sm uppercase tracking-[0.18em] font-medium hover:text-[#D9480F] transition-colors"
              >
                {l.label}
              </button>
            )
          )}
        </nav>

        <a
          href="tel:4847643392"
          data-testid={NAV.callBtn}
          onClick={() => trackCallConversion("nav_call_btn")}
          className="hidden md:inline-flex items-center gap-2 bg-[#D9480F] text-white px-5 py-2.5 font-display uppercase tracking-tight text-sm hover:bg-[#E8590C] transition-colors hard-shadow-sm"
        >
          <Phone className="w-4 h-4" strokeWidth={2.5} />
          484-764-3392
        </a>

        <button
          className="md:hidden p-2 border border-zinc-900"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-900 bg-[#F4F4F5]">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) =>
              l.isRoute ? (
                <Link
                  key={l.id}
                  to={l.path}
                  onClick={() => setOpen(false)}
                  className="text-left text-sm uppercase tracking-[0.18em] font-medium"
                >
                  {l.label}
                </Link>
              ) : l.hasBusinessDropdown ? (
                <Link
                  key={l.id}
                  to="/for/realtors"
                  onClick={() => setOpen(false)}
                  className="text-left text-sm uppercase tracking-[0.18em] font-medium"
                >
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="text-left text-sm uppercase tracking-[0.18em] font-medium"
                >
                  {l.label}
                </button>
              )
            )}
            <a
              href="tel:4847643392"
              className="inline-flex items-center justify-center gap-2 bg-[#D9480F] text-white px-5 py-3 font-display uppercase tracking-tight text-sm"
            >
              <Phone className="w-4 h-4" />
              Call 484-764-3392
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default NavBar;
